import { Trans, useLingui } from "@lingui/react/macro";
import { observable } from "mobx";
import { observer } from "mobx-react-lite";
import { useState } from "react";

import {
  type UpdatePatientLevel4Schema,
  apiPutPatientsPatientId,
} from "../../../api";
import { AppModel } from "../../../app/app-model";
import { Button } from "../../../components/ui-kit/button";
import { Dialog } from "../../../components/ui-kit/dialog";
import { Select } from "../../../components/ui-kit/fields/select";
import { TextInput } from "../../../components/ui-kit/fields/text-input";
import { Form } from "../../../components/ui-kit/form/form";
import { FormRow } from "../../../components/ui-kit/form/form-row";
import { FormTable } from "../../../components/ui-kit/form/form-table";
import {
  healthProPatientDiagnoses,
  healthProPhysicians,
} from "../../../constants/enum-to-text";

type HealthProFields = {
  healthpro_id: string | null;
  healthpro_diagnosis: string;
  healthpro_physician: string;
};

function toUpdatePayload(fields: HealthProFields): UpdatePatientLevel4Schema {
  return {
    insurance_policy_details: {
      healthpro_id: fields.healthpro_id,
      healthpro_diagnosis: fields.healthpro_diagnosis,
      healthpro_physician: fields.healthpro_physician,
    },
  };
}

export const AddHealthProDialog = observer(function AddHealthProDialog({
  app,
  onClose,
}: {
  app: AppModel;
  onClose: () => void;
}) {
  const { t } = useLingui();
  const patient = app.requirePatient();
  const [fields] = useState<HealthProFields>(() =>
    observable({
      healthpro_id: null,
      healthpro_diagnosis: "I50.9",
      healthpro_physician: "diana_sevcikova",
    }),
  );

  const hasValidFields =
    !!fields.healthpro_id &&
    !!fields.healthpro_diagnosis &&
    !!fields.healthpro_physician;

  async function save() {
    if (!hasValidFields) return;
    try {
      await apiPutPatientsPatientId({
        path: { patient_id: patient.patientId },
        body: toUpdatePayload(fields),
      });
      await patient.fetch();
      onClose();
    } catch (error) {
      window.handleError(error);
    }
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <h3 className="mb-6 text-base font-bold">
        <Trans>Add HealthPro details</Trans>
      </h3>
      <Form onSubmit={() => save()}>
        <FormTable big>
          <FormRow
            label={t`HealthPro ID`}
            data={<TextInput attr="healthpro_id" model={fields} width={100} />}
            mandatory
          />
          <FormRow
            label={t`HealthPro main diagnosis`}
            data={
              <Select
                attr="healthpro_diagnosis"
                model={fields}
                values={healthProPatientDiagnoses}
                required
              />
            }
            mandatory
          />
          <FormRow
            label={t`HealthPro physician`}
            data={
              <Select
                attr="healthpro_physician"
                model={fields}
                values={healthProPhysicians}
                required
              />
            }
            mandatory
          />
        </FormTable>
        <div className="mt-6 flex justify-center gap-2">
          <Button type="submit" disabled={!hasValidFields}>
            <Trans>Add details</Trans>
          </Button>
          <Button
            type="button"
            variant="subtle"
            size="default"
            onClick={onClose}
          >
            <Trans>Cancel</Trans>
          </Button>
        </div>
      </Form>
    </Dialog>
  );
});
